import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LoadNote {
  id: string;
  direct_load_id: string;
  author_user_id: string;
  author_role: "shipper" | "broker";
  author_company_name: string | null;
  body: string;
  created_at: string;
}

/**
 * Shared shipper/broker note thread for an LR Direct load.
 * RLS restricts reads + inserts to the load's shipper owner and the awarded
 * broker's company members, so no client-side filtering is required.
 */
export function useLoadNotes(loadId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ["lr-direct-load-notes", loadId];

  const query = useQuery<LoadNote[]>({
    queryKey,
    enabled: !!loadId,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("direct_load_notes" as any)
        .select("*")
        .eq("direct_load_id", loadId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data as any[] as LoadNote[]) ?? [];
    },
  });

  // Realtime — both sides see new notes without a refresh.
  useEffect(() => {
    if (!loadId) return;
    const channel = supabase
      .channel(`load-notes-${loadId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "direct_load_notes",
          filter: `direct_load_id=eq.${loadId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadId]);

  const addNote = useMutation({
    mutationFn: async (input: {
      body: string;
      authorUserId: string;
      authorRole: "shipper" | "broker";
      authorCompanyName: string | null;
    }) => {
      const body = input.body.trim();
      if (!body) throw new Error("Note cannot be empty");
      if (body.length > 2000) throw new Error("Note is too long (2000 characters max)");

      const { data, error } = await supabase
        .from("direct_load_notes" as any)
        .insert({
          direct_load_id: loadId,
          author_user_id: input.authorUserId,
          author_role: input.authorRole,
          author_company_name: input.authorCompanyName,
          body,
        })
        .select("*");

      // Silent-write guard: a blocked insert returns zero rows with no error.
      if (error) throw error;
      if (!data || (data as any[]).length === 0) {
        throw new Error("Note was not saved — you may no longer have access to this load.");
      }
      return (data as any[])[0] as LoadNote;
    },
    onSuccess: () => {
      // Refetch-on-post fallback in case realtime is not connected.
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return { notes: query.data ?? [], isLoading: query.isLoading, error: query.error, addNote };
}

/** Full legal company name of the signed-in user's primary company (snapshot source). */
export function useMyCompanyName(userId: string | undefined) {
  return useQuery<string | null>({
    queryKey: ["my-company-name", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("company_users")
        .select("company_id, is_primary, companies(name)")
        .eq("user_id", userId!)
        .eq("is_active", true)
        .order("is_primary", { ascending: false })
        .limit(1)
        .maybeSingle();
      return ((data as any)?.companies?.name as string) ?? null;
    },
  });
}
